<template>
  <b-card :title="isEdit ? 'Editar Conta' : 'Nova Conta'">
    <b-form @submit.prevent="save">
      <b-row>
        <!-- Row 1 -->
        <b-col md="6">
          <b-form-group label="Nome da Conta *">
            <b-form-input
              v-model="form.descricao"
              required
              placeholder="Ex: Caixa Interno, Conta Itaú Principal..."
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Tipo de Conta *">
            <v-select
              v-model="form.tipoConta"
              :options="tiposContaOptions"
              label="descricao"
              required
              placeholder="Selecione o tipo de conta"
            />
          </b-form-group>
        </b-col>

        <!-- Row 2 -->
        <b-col md="6">
          <b-form-group label="Banco (Opcional se for Caixa)">
            <v-select
              v-model="form.banco"
              :options="bancosOptions"
              label="descricao"
              placeholder="Selecione o banco"
            >
              <template #option="{ descricao, logoUrl }">
                <div class="d-flex align-items-center">
                  <b-img
                    v-if="logoUrl"
                    :src="logoUrl"
                    width="20"
                    height="20"
                    class="mr-50 rounded border bg-light"
                    style="object-fit: contain"
                  />
                  <span>{{ descricao }}</span>
                </div>
              </template>
              <template #selected-option="{ descricao, logoUrl }">
                <div class="d-flex align-items-center">
                  <b-img
                    v-if="logoUrl"
                    :src="logoUrl"
                    width="20"
                    height="20"
                    class="mr-50 rounded border bg-light"
                    style="object-fit: contain"
                  />
                  <span>{{ descricao }}</span>
                </div>
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        
        <b-col md="2">
          <b-form-group label="Agência">
            <b-form-input
              v-model="form.agencia"
              placeholder="0000"
            />
          </b-form-group>
        </b-col>

        <b-col md="3">
          <b-form-group label="Conta">
            <b-form-input
              v-model="form.conta"
              placeholder="00000"
            />
          </b-form-group>
        </b-col>

        <b-col md="1">
          <b-form-group label="Dígito">
            <b-form-input
              v-model="form.digitoVerificador"
              placeholder="0"
            />
          </b-form-group>
        </b-col>

        </b-col>

        <!-- Row 3 -->
        <b-col v-if="isEdit" md="12" class="mb-1">
          <b-alert show variant="warning" class="mb-0">
            <div class="alert-body d-flex align-items-center">
              <feather-icon icon="AlertTriangleIcon" class="mr-50" />
              <span>A alteração da <strong>Data</strong> ou do <strong>Valor Inicial</strong> forçará o recálculo automático de todo o saldo da conta no banco de dados.</span>
            </div>
          </b-alert>
        </b-col>

        <b-col md="3">
          <b-form-group label="Saldo Inicial (R$)">
            <money
              v-model="form.valorSaldoInicial"
              v-bind="money"
              class="form-control text-right"
            />
          </b-form-group>
        </b-col>

        <b-col md="3">
          <b-form-group label="Data Saldo Inicial">
            <flat-pickr
              v-model="form.dataSaldoInicial"
              class="form-control"
              :config="configDate"
              placeholder="Selecione uma data"
            />
          </b-form-group>
        </b-col>

        <b-col md="3">
          <b-form-group label="Moeda">
            <v-select
              v-model="form.moeda"
              :options="moedasOptions"
              label="descricao"
              placeholder="Selecione a moeda"
            >
              <template #option="{ descricao, nome }">
                <span>{{ descricao }} {{ nome }}</span>
              </template>
              <template #selected-option="{ descricao, nome }">
                <span>{{ descricao }} {{ nome }}</span>
              </template>
            </v-select>
          </b-form-group>
        </b-col>

        <b-col md="12">
          <b-form-group label="Integração Bancária (Gateway para Boleto/PIX/Cartão)">
            <v-select
              v-model="form.configuracaoGateway"
              :options="gatewaysOptions"
              label="descricao"
              placeholder="Selecione um gateway para automatizar cobranças nesta conta"
            >
              <template #option="{ descricao, provedor, ambiente }">
                <div class="d-flex flex-column">
                  <strong>{{ descricao }}</strong>
                  <small>{{ provedor }} - {{ ambiente }}</small>
                </div>
              </template>
              <template #selected-option="{ descricao, provedor }">
                <span>{{ descricao }} ({{ provedor }})</span>
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        
        <!-- Booleans -->
        <b-col md="3" class="mt-2">
          <div class="d-flex flex-column gap-1">
            <b-form-checkbox v-model="form.principal" switch class="mb-50">
              Conta Principal
            </b-form-checkbox>
            <b-form-checkbox v-model="form.permiteSaldoNegativo" switch class="mb-50">
              Permite Saldo Negativo
            </b-form-checkbox>
            <b-form-checkbox v-model="form.ativo" switch>
              Conta Ativa
            </b-form-checkbox>
          </div>
        </b-col>

        <!-- Descrição removida pois 'descricao' é o Nome da conta no banco de dados -->
      </b-row>

      <hr />
      
      <div class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="mr-1" @click="cancel">
          Cancelar
        </b-button>
        <b-button type="submit" variant="primary">
          <feather-icon icon="SaveIcon" class="mr-50" />
          {{ isEdit ? 'Salvar Alterações' : 'Cadastrar Conta' }}
        </b-button>
      </div>
    </b-form>
  </b-card>
</template>

<script>
import {
  BRow, BCol, BCard, BForm, BFormGroup, BFormInput, BFormSelect, BFormSelectOption, BFormCheckbox, BFormTextarea, BButton, BAlert, BImg
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { Money } from 'v-money'
import flatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import { mapState } from 'vuex'
import mixinsGlobal from '@/mixins'
import paymentGatewayServices from '@/api/financial/paymentGatewayServices'

export default {
  name: 'ContaForm',
  components: {
    BRow, BCol, BCard, BForm, BFormGroup, BFormInput, BFormSelect, BFormSelectOption, BFormCheckbox, BFormTextarea, BButton, BAlert,
    vSelect,
    Money,
    flatPickr,
    BImg
  },
  mixins: [mixinsGlobal],
  data() {
    return {
      isEdit: false,
      form: {
        id: '',
        descricao: '',
        tipoConta: null,
        banco: null,
        agencia: '',
        conta: '',
        digitoVerificador: '',
        dataSaldoInicial: null,
        valorSaldoInicial: 0.0,
        moeda: null,
        principal: false,
        permiteSaldoNegativo: false,
        ativo: true,
        configuracaoGateway: null
      },
      gatewaysOptions: [],
      configDate: {
        altInput: true,
        altFormat: 'd/m/Y',
        dateFormat: 'Y-m-d',
        locale: Portuguese
      },
      money: {
        decimal: ',',
        thousands: '.',
        prefix: '',
        suffix: '',
        precision: 2,
        masked: false
      }
    }
  },
  computed: {
    ...mapState('contaFinancialModule', ['conta']),
    ...mapState('combosModule', {
      bancosOptions: state => state.comboBancos,
      tiposContaOptions: state => state.comboTipoContas,
      moedasOptions: state => state.comboMoedas
    })
  },
  async mounted() {
    await this.loadCombos()
    await this.loadGateways()
    if (this.$route.params.id || this.conta.id) {
      this.isEdit = true
      this.loadContaForEdit()
    } else {
      this.$store.dispatch('contaFinancialModule/reset')
      this.form = Object.assign({}, this.$store.state.contaFinancialModule.conta)
    }
  },
  methods: {
    async loadCombos() {
      await Promise.all([
        this.$store.dispatch('combosModule/loadComboBancos'),
        this.$store.dispatch('combosModule/loadComboTipoConta'),
        this.$store.dispatch('combosModule/loadComboMoedas')
      ])
    },
    async loadGateways() {
      try {
        const { data } = await paymentGatewayServices.loadAll()
        this.gatewaysOptions = data
      } catch (e) {
        console.error('Erro ao carregar gateways', e)
      }
    },
    async loadContaForEdit() {
      const id = this.$route.params.id || this.conta.id
      try {
        const response = await this.$store.dispatch('contaFinancialModule/findById', id)
        if (response && response.data) {
          const dt = response.data
          this.form = {
            id: dt.id,
            descricao: dt.descricao,
            tipoConta: dt.tipoConta,
            banco: dt.banco,
            agencia: dt.agencia,
            conta: dt.conta,
            digitoVerificador: dt.digitoVerificador,
            dataSaldoInicial: dt.dataSaldoInicial,
            valorSaldoInicial: dt.valorSaldoInicial || 0.0,
            moeda: dt.moeda,
            principal: dt.principal,
            permiteSaldoNegativo: dt.permiteSaldoNegativo,
            ativo: dt.ativo,
            configuracaoGateway: this.gatewaysOptions.find(g => g.id === (dt.configuracaoGateway ? dt.configuracaoGateway.id : null))
          }
        }
      } catch (e) {
        this.MensagemError('Erro', 'Não foi possível carregar a Conta.')
        this.cancel()
      }
    },
    async save() {
      // Validate
      if (!this.form.descricao) {
        this.MensagemError('Validação', 'O nome da conta é obrigatório.')
        return
      }

      const payload = { ...this.form }
      
      let action = 'contaFinancialModule/insert'
      if (this.isEdit) {
        action = 'contaFinancialModule/update'
      }

      try {
        const response = await this.$store.dispatch(action, payload)
        if (response && response.data && response.data.id) {
          this.showMessageSuccess('Sucesso', `Conta ${this.isEdit ? 'atualizada' : 'cadastrada'} com sucesso!`)
          this.$router.push({ name: 'contas' })
        } else if (response.response && response.response.data && response.response.data.errors) {
          this.MensagemError('Erro', response.response.data.errors[0].message)
        } else {
          this.MensagemError('Erro', `Falha ao ${this.isEdit ? 'atualizada' : 'cadastrada'} a conta.`)
        }
      } catch (error) {
        this.MensagemError('Erro', 'Ocorreu um erro na requisição.')
      }
    },
    cancel() {
      this.$router.push({ name: 'contas' })
    }
  }
}
</script>

<style scoped>
.gap-1 { gap: 0.25rem; }
</style>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
